import * as React from 'react';
import { FC } from 'react';
import Field from './Field';
import TargetSelect from './TargetSelect';
import RowTarget from './RowTarget';
import { useContext } from './useFormContext';
import { UpdateValidationState } from './useValidationState';

const ArgentinaBankWithdrawalDetails: FC<{
  updateValidationState: UpdateValidationState;
}> = ({ updateValidationState }) => {
  const { locales } = useContext();

  return (
    <>
      <Field
        label={locales.target_label}
        name="target"
        validation="required"
        updateValidationState={updateValidationState}
        placeholder={locales.target_placeholder}
      />
      <Field
        label={locales.tax_id_label}
        name="tax_id"
        validation="required"
        updateValidationState={updateValidationState}
        placeholder={locales.tax_id_placeholder}
      />
    </>
  );
};

const CryptoWithdrawalDetails: FC<{
  vendorCurrency: string;
  updateValidationState: UpdateValidationState;
}> = ({ vendorCurrency, updateValidationState }) => {
  const { locales, extra } = useContext();
  const network = extra.networks[vendorCurrency];

  return (
    <>
      {network && (
        <div
          className="rs-text-size_small alert"
          style={{ textAlign: 'center', border: '2px solid orange', padding: '0.5em 1em', marginBottom: '1em' }}
        >
          <div style={{ fontSize: '0.6rem' }}>{locales.network}</div>
          <div style={{ marginBottom: '-0.3rem', fontFamily: 'Arial' }}>
            <b>{network[0]}</b>
          </div>
          <div>{network[1]}</div>
        </div>
      )}
      <Field
        label={locales.target_label}
        name="target"
        validation="required"
        updateValidationState={updateValidationState}
        placeholder={locales.target_placeholder}
      />
      {extra.payments_with_tag.includes(vendorCurrency) ? (
        <Field
          label="Memo (tag)"
          name="target_extra"
          validation="required"
          updateValidationState={updateValidationState}
        />
      ) : null}
      <div className="rs-row">
        <div className="rs-column-small-12">
          <p className="rs-text-size_small">{locales.hint}</p>
        </div>
      </div>
    </>
  );
};

const AppendFields: FC<{ vendorCurrency: string; updateValidationState: UpdateValidationState }> = ({
  vendorCurrency,
  updateValidationState,
}) => {
  const { key, locales, extra } = useContext();

  switch (key) {
    case 'InitialCardDeposit': {
      const warning = (locales.warnings as unknown as Record<string, string>)?.[vendorCurrency];
      return warning ? (
        <div className="rs-column-small-12">
          <p className="error">{warning}</p>
        </div>
      ) : null;
    }

    case 'mobile_withdrawal':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="target_extra[service_provider]">{locales.service_provider}:</label>
            <select name="target_extra[service_provider]" required>
              {locales.service_providers.map((code) => (
                <option key={code} value={code}>
                  {locales.service_provider_names[code] || code}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="phoneWithCode" />
          </div>
        </div>
      );

    case 'argentina_bank_deposit/apipay':
    case 'cis_bank_withdrawal':
    case 'ecp2025_deposit/alfaclick':
    case 'ecp2025_withdrawal/binancepay':
    case 'ecp2025_withdrawal/iwallet':
    case 'ecp2025_withdrawal/monetix':
    case 'ecp2025_withdrawal/sepa':
      return <RowTarget updateValidationState={updateValidationState} validation="required" />;

    case 'argentina_bank_withdrawal':
      return <ArgentinaBankWithdrawalDetails updateValidationState={updateValidationState} />;

    case 'crypto_withdrawal':
      return <CryptoWithdrawalDetails vendorCurrency={vendorCurrency} updateValidationState={updateValidationState} />;

    case 'ecp2025_withdrawal/asia_indonesia':
    case 'ecp2025_withdrawal/asia_malaysia':
    case 'ecp2025_withdrawal/asia_philippines':
    case 'ecp2025_withdrawal/asia_thailand':
    case 'ecp2025_withdrawal/asia_vietnam':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="target_extra[bank_id]">{locales.bank_id}:</label>
            <select name="target_extra[bank_id]" id="target_extra[bank_id]" required>
              {(locales.banks as unknown as [string, number][]).map((bank) => (
                <option key={bank[1]} value={bank[1]}>
                  {bank[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="required" />
          </div>
          <div className="rs-column-small-12">
            <label htmlFor="asia_beneficiary">{locales.beneficiary}:</label>
            <input
              type="text"
              disabled
              readOnly
              name="asia_beneficiary"
              value={`${extra.first_name} ${extra.last_name}`}
            />
            <p className="rs-text-size_small">
              {locales.can_be_changed_in}
              &nbsp;
              <a href="/player">{locales.player_profile}</a>
            </p>
          </div>
        </div>
      );

    case 'ecp2025_deposit/cashtocode':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">
            {(locales.warnings as unknown as Record<string, string>)?.[extra.country]}
          </p>
        </div>
      );

    case 'oneclick_deposit':
    case 'oneclick_deposit/bova':
    case 'sbp_deposit':
    case 'sbp_deposit/bova':
    case 'sbp_deposit/aifory_tbank':
      return (
        <div className="rs-column-small-12">
          <label>{locales.bank_name}:</label>
          <div className="radio-group">
            {(locales.service_providers as unknown as string[]).map((code) => (
              <span>
                <input
                  type="radio"
                  name="bank_name"
                  id={`bank_code_${code}`}
                  value={code}
                  required
                  checked={locales.service_providers.length === 1 || undefined}
                />
                <label htmlFor={`bank_code_${code}`}>{locales.service_provider_names[code] || code}</label>
              </span>
            ))}
          </div>
        </div>
      );

    case 'sbp_withdrawal':
    case 'sbp_fast_withdrawal':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="bank_name">{locales.bank_name}:</label>
            <select name="bank_name" id="bank_name" required>
              {(locales.banks as unknown as string[]).map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="required" />
          </div>
        </div>
      );
    case 'ecp2025_deposit/bloomzed':
    case 'ecp2025_deposit/qiwi':
    case 'ecp2025_deposit/ua_mobile':
    case 'ecp2025_deposit/mobile':
    case 'ecp2025_withdrawal/bloomzed':
      return <RowTarget updateValidationState={updateValidationState} validation="phoneWithCode" />;

    case 'manual_deposit':
    case 'manual_withdrawal':
    case 'user_withdrawal':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">{locales.warning}</p>
        </div>
      );

    case 'ecopayz_withdrawal':
    case 'ecp2025_withdrawal/advcash':
    case 'ecp2025_withdrawal/moneta':
    case 'ecp2025_withdrawal/qiwi':
    case 'ecp2025_withdrawal/yandex':
    case 'neteller_withdrawal':
    case 'settlepay_withdrawal':
    case 'skrill_withdrawal':
    case 'webmoney_withdrawal':
    case 'perfect_money_withdrawal':
      return <TargetSelect vendorCurrency={vendorCurrency} updateValidationState={updateValidationState} />;

    case 'ecp2025_withdrawal/interac_etransfer':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">{locales.warning}:</p>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.email}: </span>
            <span>{extra.email}</span>
          </div>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.phone}: </span>
            <span>{extra.phone}</span>
          </div>
        </div>
      );

    case 'ecp2025_withdrawal/interac_ecashout':
      return (
        <div className="rs-flex">
          <div className="rs-flex-item rs-small-3">
            <Field
              validation="html5"
              name="target_extra[iban]"
              label="Transit #"
              placeholder="12345"
              required
              length={5}
              pattern="^\d+$"
              updateValidationState={updateValidationState}
            />
          </div>
          <div className="rs-flex-item rs-small-2">
            <Field
              validation="html5"
              name="target_extra[fin]"
              label="FIN"
              placeholder="567"
              required
              length={3}
              pattern="^\d+$"
              updateValidationState={updateValidationState}
            />
          </div>
          <div className="rs-flex-item rs-small-7">
            <Field
              validation="html5"
              name="target_extra[number]"
              label="Account #"
              placeholder="1234567"
              required
              maxLength={12}
              pattern="^\d+$"
              updateValidationState={updateValidationState}
            />
          </div>
        </div>
      );

    case 'ecp2025_withdrawal/pix':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} />
          </div>
          <div className="rs-column-small-12">
            <Field
              validation="html5"
              name="target_extra[cpf]"
              label="Your Brazilian CPF"
              placeholder="12345678901"
              required
              length={11}
              pattern="^\d+$"
              updateValidationState={updateValidationState}
            />
          </div>
          <div className="rs-column-small-12">
            <label htmlFor="target_extra[bank_id]">{locales.bank_id}</label>
            <select name="target_extra[bank_id]" required>
              {(locales.banks as unknown as [string, number][]).map((bank) => (
                <option key={bank[1]} value={bank[1]}>
                  {bank[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <Field
              validation="html5"
              name="target_extra[bank_branch_code]"
              label={locales.bank_branch_code}
              required
              updateValidationState={updateValidationState}
            />
          </div>
          <div className="rs-flex rs-gap_small__zero">
            <div className="rs-flex-item rs-medium-6 rs-small-12">
              <Field
                validation="html5"
                name="target_extra[account_number]"
                label={locales.account_number}
                placeholder=""
                required
                pattern="^\d+$"
                updateValidationState={updateValidationState}
              />
            </div>
            <div className="rs-flex-item rs-medium-6 rs-small-12">
              <label htmlFor="target_extra[account_type]">{locales.account_type}</label>
              <select name="target_extra[account_type]" required>
                {(locales.account_types as unknown as string[]).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default AppendFields;
